import React from 'react';
import ListItem from '@smart-tv/components/list';
import { FocusHandler } from '@noriginmedia/norigin-spatial-navigation';
import getPlaybackDevices from '@smart-tv/utils/kmm/getPlaybackDevices';
import { MAX_DEVICES_LIMIT } from '@smart-tv/components/player/player-devices/player-devices-context';

// Add SIDs here to check device limits for specific channels
const TEST_SIDS: { label: string; sid: number }[] = [{ label: 'TV2', sid: 156 }];

interface Props {
  listItemClassName?: string;
  onFocus?: FocusHandler;
}

function useDeviceCheck(sid: number) {
  const [result, setResult] = React.useState<string>('Press to run');
  const [running, setRunning] = React.useState(false);

  const run = React.useCallback(async () => {
    if (running) return;
    setRunning(true);
    setResult('Running…');
    try {
      const data = await getPlaybackDevices({ sid });
      const active = data.devices.filter((d) => d.active).length;
      const total = data.devices.length;
      const rating =
        active >= MAX_DEVICES_LIMIT
          ? 'Limit reached'
          : `${MAX_DEVICES_LIMIT - active} slot(s) free`;
      setResult(`${active} active, ${total} total — ${rating}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : JSON.stringify(err);
      setResult(`Error: ${message}`);
    } finally {
      setRunning(false);
    }
  }, [running, sid]);

  return { result, run };
}

const SidRow: React.FC<{ label: string; sid: number; className?: string; onFocus?: FocusHandler }> = ({
  label,
  sid,
  className,
  onFocus
}) => {
  const { result, run } = useDeviceCheck(sid);
  return (
    <ListItem
      listItemProps={{ className }}
      onEnterPress={run}
      onFocus={onFocus}
      data={[`${label} (sid ${sid})`, result]}
    />
  );
};

const DeveloperActiveDevices: React.FC<Props> = ({ listItemClassName, onFocus }) => {
  if (TEST_SIDS.length === 0) {
    return (
      <ListItem
        listItemProps={{ className: listItemClassName }}
        data={['Active Streams', 'No SIDs configured']}
      />
    );
  }

  return (
    <>
      {TEST_SIDS.map(({ label, sid }) => (
        <SidRow key={sid} label={label} sid={sid} className={listItemClassName} onFocus={onFocus} />
      ))}
    </>
  );
};

export default DeveloperActiveDevices;
